import { useNotificationCreatorBase } from './useNotificationCreatorBase.js'
import type { NotificationLinkType, NotificationPriority } from '~/shared/types/notification.js'

/**
 * Composable for admin-related notifications
 */
export function useAdminNotifications() {
  const {
    notificationCore,
  } = useNotificationCreatorBase()

  /**
   * Notify admins about a critical system event
   */
  const createAdminAlertNotification = async (
    title: string,
    body: string,
    options?: {
      priority?: NotificationPriority
      linkType?: NotificationLinkType
      linkTarget?: string
      sendEmail?: boolean
    },
  ): Promise<string[]> => {
    try {
      const meta: Record<string, unknown> = { isAdminAlert: true }
      if (options?.linkType === 'member' && options?.linkTarget) {
        meta.userId = options.linkTarget
      }
      const res = await $fetch<{ success?: boolean, message?: string, adminsCount?: number }>(
        '/api/notifications/notify-admins',
        {
          method: 'POST',
          body: {
            title,
            body,
            metadata: meta,
            priority: options?.priority || 'high',
            linkType: options?.linkType,
            linkTarget: options?.linkTarget,
            sendEmail: options?.sendEmail !== false,
            emailTemplate: 'admin-alert',
          },
        },
      )

      if (!res?.success) {
        console.warn('[AdminNotifications] notify-admins endpoint responded without success:', res?.message)
      }
    }
    catch (err) {
      console.error('[AdminNotifications] Failed to call notify-admins endpoint:', err)
    }
    // Endpoint does not return per-notification IDs; return empty list to satisfy signature
    return []
  }

  /**
   * Notify an admin about a member's ban or unban
   */
  const createMemberBanStatusNotification = async (
    adminId: string,
    memberId: string,
    memberName: string,
    banned: boolean,
    reason?: string,
  ): Promise<string> => {
    const action = banned ? 'banned' : 'unbanned'
    const title = `Member ${action}: ${memberName}`
    let body = `${memberName} (${memberId}) has been ${action}.`

    if (banned && reason) {
      body += `\n\nReason: ${reason}`
    }

    return notificationCore.createNotificationForMember(adminId, {
      title,
      body,
      category: 'admin',
      priority: 'normal',
      linkType: 'member',
      linkTarget: memberId,
      emailScheduled: false,
      metadata: {
        emailTemplate: 'member-ban-status-change',
        emailData: {
          memberName,
          memberId,
          banned,
          reason: reason || '',
        },
      },
    })
  }

  return {
    createAdminAlertNotification,
    createMemberBanStatusNotification,
  }
}
